import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout, { PageHeader } from "@/components/Layout";
import { api, fmtINR, fmtNum, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Zap, Save, Ruler, Plus, Trash2, Package, FileText } from "lucide-react";
import { Subtotals } from "@/lib/subtotals";
import RackDiagram from "@/components/RackDiagram";
import PalletBomModal from "@/components/PalletBomModal";
import { useAuth } from "@/contexts/AuthContext";

const defaults = {
  height: 3000,
  length: 2700,
  depth: 1000,
  levels: 3,
  weight_capacity_per_level: 1000,
  unit_type: "main",
};

function NumInput({ label, unit, value, onChange, testid, hint, min = 0 }) {
  return (
    <div>
      <label className="label-tech mb-2 flex items-center justify-between">
        <span>{label}</span>
        {unit && <span className="text-ink-faint normal-case tracking-normal mono">{unit}</span>}
      </label>
      <input
        type="number"
        min={min}
        value={value}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        className="input-tech mono"
        data-testid={testid}
      />
      {hint && <div className="text-[0.7rem] text-ink-muted mt-1.5">{hint}</div>}
    </div>
  );
}

export default function Configurator() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDealer = user?.role === "dealer";

  const [cfg, setCfg] = useState(defaults);
  const [quantity, setQuantity] = useState(1);
  const [label, setLabel] = useState("");
  const [result, setResult] = useState(null);
  const [computing, setComputing] = useState(false);
  const [pricing, setPricing] = useState(null);

  React.useEffect(() => {
    api.get("/pricing").then((r) => setPricing(r.data)).catch(() => {});
  }, []);

  // Line-items cart
  const [items, setItems] = useState([]);
  const [client, setClient] = useState({
    name: "", company: "", contact: "", address: "", gstin: "",
  });
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  // BOM modal state (admin/employee only)
  const canUseBom = user?.role === "admin" || user?.role === "employee";
  const [bomOpen, setBomOpen] = useState(false);
  const [bomRows, setBomRows] = useState([]);
  const [bomBusy, setBomBusy] = useState(false);

  const update = (k) => (v) => setCfg((p) => ({ ...p, [k]: v }));

  const compute = async () => {
    setComputing(true);
    setResult(null);
    try {
      const { data } = await api.post("/calculate", cfg);
      setResult(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setComputing(false);
    }
  };

  const addToQuotation = () => {
    if (!result) {
      toast.error("Compute the configuration first");
      return;
    }
    const qty = Math.max(1, parseInt(quantity) || 1);
    setItems((prev) => [
      ...prev,
      { config: { ...cfg }, quantity: qty, label: label.trim(), result },
    ]);
    toast.success(`Added ${qty} × ${cfg.unit_type === "addon" ? "Add-on" : "Main"} to quotation`);
    setLabel("");
    setQuantity(1);
    // Keep result visible so user can quickly duplicate with tweaks
  };

  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));
  const bumpQty = (idx, delta) =>
    setItems((prev) =>
      prev.map((it, i) =>
        i === idx ? { ...it, quantity: Math.max(1, it.quantity + delta) } : it
      )
    );

  const cartTotals = items.reduce(
    (acc, it) => {
      acc.weight += it.result.total_weight_kg * it.quantity;
      acc.price += it.result.total_price * it.quantity;
      acc.count += it.quantity;
      return acc;
    },
    { weight: 0, price: 0, count: 0 }
  );

  const gstPercent = Number(pricing?.gst_percent || 0);
  const gstAmount = cartTotals.price * gstPercent / 100;
  const grandTotal = cartTotals.price + gstAmount;

  const saveQuote = async () => {
    if (items.length === 0) {
      toast.error("Add at least one item to the quotation");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        client,
        notes,
        items: items.map((it) => ({
          config: it.config,
          quantity: it.quantity,
          label: it.label || "",
        })),
      };
      const { data } = await api.post("/quotations", payload);
      toast.success(`Quotation ${data.quote_number} saved`);
      navigate(`/quotations/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  // ----- BOM (cutting list) -----
  const openBom = async () => {
    // Allow BOM either from the saved cart or from the live (un-added) result.
    const linesToBom = items.length
      ? items.map((it) => ({ config: it.config, quantity: it.quantity, label: it.label || "" }))
      : (result
          ? [{ config: { ...cfg }, quantity: Math.max(1, parseInt(quantity) || 1), label: label.trim() }]
          : []);
    if (linesToBom.length === 0) {
      toast.error("Add at least one rack (or compute one) to build a BOM");
      return;
    }
    setBomOpen(true);
    setBomBusy(true);
    setBomRows([]);
    try {
      const { data } = await api.post("/calculate/bom", { client, notes, items: linesToBom });
      setBomRows(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setBomOpen(false);
    } finally {
      setBomBusy(false);
    }
  };

  return (
    <Layout>
      <PageHeader
        eyebrow="Configurator · 01"
        title="Rack Configuration"
        subtitle="Configure each rack, add to the quotation, then save. Mix main and add-on units with any quantity."
      />

      <div className="p-8 grid grid-cols-1 xl:grid-cols-5 gap-px bg-line border border-line">
        {/* Inputs */}
        <section className="xl:col-span-2 bg-white p-6" data-testid="inputs-section">
          <div className="flex items-center gap-2 mb-5">
            <Ruler size={18} className="text-brand" />
            <div className="label-tech">Dimensions & Load</div>
          </div>

          <div className="mb-5">
            <label className="label-tech mb-2 block">Unit type</label>
            <div className="flex items-center gap-px border border-line bg-line w-fit" data-testid="unit-type-toggle">
              {[
                { k: "main", l: "Main unit" },
                { k: "addon", l: "Add-on" },
              ].map((o) => (
                <button
                  key={o.k}
                  type="button"
                  onClick={() => setCfg((p) => ({ ...p, unit_type: o.k }))}
                  data-testid={`unit-type-${o.k}`}
                  className={`px-5 py-2 text-xs font-bold uppercase tracking-wider ${
                    cfg.unit_type === o.k ? "bg-ink text-white" : "bg-white text-ink-muted hover:text-ink"
                  }`}
                >
                  {o.l}
                </button>
              ))}
            </div>
            <div className="text-[0.7rem] text-ink-muted mt-1.5">
              {cfg.unit_type === "addon"
                ? "Add-on shares an upright with an adjacent main unit — only 1 side of uprights is charged."
                : "Standalone rack with uprights on both sides."}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-5">
            <NumInput label="Height" unit="mm" value={cfg.height} onChange={update("height")} testid="input-height" />
            <NumInput label="Length" unit="mm" value={cfg.length} onChange={update("length")} testid="input-length" />
            <NumInput label="Depth" unit="mm" value={cfg.depth} onChange={update("depth")} testid="input-depth" />
            <NumInput label="Levels" unit="count" value={cfg.levels} onChange={update("levels")} testid="input-levels" min={1} />
            <div className="col-span-2">
              <NumInput
                label="Weight capacity per level"
                unit="kg"
                value={cfg.weight_capacity_per_level}
                onChange={update("weight_capacity_per_level")}
                testid="input-capacity"
              />
            </div>
          </div>

          <button onClick={compute} disabled={computing} className="btn-primary w-full mt-6" data-testid="calc-btn">
            <Zap size={16} /> {computing ? "Computing…" : "Compute configuration"}
          </button>

          {result && (
            <div className="mt-5 grid grid-cols-2 gap-4 items-end">
              <div>
                <label className="label-tech mb-2 block">Quantity</label>
                <input
                  type="number"
                  min={1}
                  className="input-tech mono"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  data-testid="item-quantity"
                />
              </div>
              <div>
                <label className="label-tech mb-2 block">Label (optional)</label>
                <input
                  className="input-tech"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="e.g. A-row"
                  data-testid="item-label"
                />
              </div>
              <button onClick={addToQuotation} className="btn-primary col-span-2" data-testid="add-to-quote-btn">
                <Plus size={16} /> Add to quotation
              </button>
            </div>
          )}
        </section>

        {/* Result */}
        <section className="xl:col-span-3 bg-white p-6" data-testid="result-section">
          <div className="label-tech mb-5">Spec Sheet</div>
          {!result ? (
            <div className="panel-heavy p-10 min-h-[400px] flex items-center justify-center bp-grid">
              <div className="text-center">
                <div className="mono text-6xl font-bold text-ink-faint mb-4">—</div>
                <div className="label-tech">Enter inputs and compute to see the spec sheet</div>
              </div>
            </div>
          ) : (
            <div className="panel-heavy" data-testid="spec-sheet">
              <div className="px-6 py-4 border-b-2 border-ink flex items-center justify-between">
                <div>
                  <div className="label-tech">
                    Specification · {cfg.unit_type === "addon" ? "Add-on unit" : "Main unit"}
                  </div>
                  <div className="display font-black text-2xl uppercase tracking-tighter">
                    {cfg.weight_capacity_per_level} kg / level
                  </div>
                </div>
                {!isDealer ? (
                  <div className="text-right">
                    <div className="label-tech">Steel density</div>
                    <div className="mono font-semibold">7.85e-6 kg/mm³</div>
                  </div>
                ) : (
                  <div className="text-right">
                    <div className="label-tech">Spec</div>
                    <div className="mono font-semibold">{cfg.levels}L · {cfg.height}×{cfg.length}×{cfg.depth}mm</div>
                  </div>
                )}
              </div>

              <table className="w-full grid-lines">
                <thead>
                  <tr>
                    <th>Component</th>
                    {!isDealer && <th>Section</th>}
                    <th className="num">Thickness</th>
                    {!isDealer && <th className="num">Approx unit wt</th>}
                    <th className="num">Qty</th>
                    <th className="num">Total wt</th>
                  </tr>
                </thead>
                <tbody>
                  {result.components.map((c) => (
                    <tr key={c.component}>
                      <td className="font-bold uppercase tracking-wider text-xs">{c.component}</td>
                      <td>{c.section_type}</td>
                      <td className="num">{c.thickness ? `${c.thickness} mm` : "—"}</td>
                      {!isDealer && <td className="num">{fmtNum(c.unit_weight_kg, 2)} kg</td>}
                      <td className="num">{c.quantity}</td>
                      <td className="num font-bold">{fmtNum(c.total_weight_kg, 2)} kg</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <Subtotals components={result.components} testid="spec-subtotals" />

              <div className="px-4 sm:px-6 py-4 grid grid-cols-1 sm:grid-cols-3 gap-3 border-t-2 border-ink">
                <div>
                  <div className="label-tech">Approx unit weight</div>
                  <div className="mono text-2xl font-bold mt-1" data-testid="result-total-weight">
                    {fmtNum(result.total_weight_kg, 2)} kg
                  </div>
                </div>
                <div>
                  <div className="label-tech">Rate</div>
                  <div className="mono text-2xl font-bold mt-1">
                    ₹{parseFloat(Number(result.price_per_kg).toFixed(2))} / kg
                  </div>
                </div>
                <div>
                  <div className="label-tech">Per-unit price</div>
                  <div className="display font-black text-3xl text-brand mt-1" data-testid="result-price">
                    {fmtINR(result.total_price)}
                  </div>
                </div>
              </div>

              {!isDealer && result.components.some((c) => c.note) && (
                <div className="px-6 py-4 border-t border-line bg-surfaceAlt">
                  <div className="label-tech mb-2">Formulas applied</div>
                  <ul className="text-xs text-ink-muted space-y-1 mono">
                    {result.components.filter((c) => c.note).map((c, i) => (
                      <li key={i}>
                        <span className="font-bold text-ink">{c.component}:</span> {c.note}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {result && (
            <div className="mt-6">
              <RackDiagram config={cfg} components={result.components} />
            </div>
          )}
        </section>
      </div>

      {/* Line items cart */}
      <div className="p-8 pt-0">
        <div className="panel" data-testid="cart-panel">
          <div className="flex items-center justify-between px-5 py-4 border-b border-line">
            <div className="flex items-center gap-2">
              <Package size={16} className="text-brand" />
              <div className="label-tech">
                Quotation items · {cartTotals.count} rack{cartTotals.count !== 1 ? "s" : ""}
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-right">
                <div className="label-tech">Total weight</div>
                <div className="mono font-bold">{fmtNum(cartTotals.weight, 2)} kg</div>
              </div>
              <div className="text-right">
                <div className="label-tech">Total price</div>
                <div className="display font-black text-2xl text-brand">{fmtINR(cartTotals.price)}</div>
              </div>
            </div>
          </div>
          {items.length === 0 ? (
            <div className="p-10 text-center text-ink-muted">
              <div className="label-tech">No items yet</div>
              <div className="text-sm mt-1">Configure a rack above and click "Add to quotation".</div>
            </div>
          ) : (
            <table className="w-full grid-lines">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Type</th>
                  <th>Label</th>
                  <th>Spec (H×L×D · Levels · Cap)</th>
                  <th className="num">Approx unit wt</th>
                  <th className="num">Unit price</th>
                  <th className="num">Qty</th>
                  <th className="num">Line total</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={idx} data-testid={`cart-row-${idx}`}>
                    <td className="mono">{idx + 1}</td>
                    <td className="label-tech">{it.config.unit_type === "addon" ? "Add-on" : "Main"}</td>
                    <td>{it.label || "—"}</td>
                    <td className="mono text-xs">
                      {it.config.height}×{it.config.length}×{it.config.depth} · {it.config.levels}L · {it.config.weight_capacity_per_level}kg
                    </td>
                    <td className="num">{fmtNum(it.result.total_weight_kg, 2)} kg</td>
                    <td className="num">{fmtINR(it.result.total_price)}</td>
                    <td className="num">
                      <div className="inline-flex items-center gap-1 border border-line">
                        <button className="px-2 py-0.5 text-ink-muted hover:text-ink" onClick={() => bumpQty(idx, -1)}>−</button>
                        <span className="mono font-bold w-8 text-center" data-testid={`cart-qty-${idx}`}>{it.quantity}</span>
                        <button className="px-2 py-0.5 text-ink-muted hover:text-ink" onClick={() => bumpQty(idx, +1)}>+</button>
                      </div>
                    </td>
                    <td className="num font-bold">{fmtINR(it.result.total_price * it.quantity)}</td>
                    <td>
                      <button className="btn-ghost text-brand-danger" onClick={() => removeItem(idx)} data-testid={`cart-remove-${idx}`}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Client + Save */}
      <div className="p-8 pt-0 grid grid-cols-1 lg:grid-cols-3 gap-px bg-line border border-line">
        <div className="lg:col-span-2 bg-white p-6">
          <div className="label-tech mb-4">Client details <span className="text-ink-faint normal-case tracking-normal">(optional)</span></div>
          <div className="grid grid-cols-2 gap-4">
            <input className="input-tech col-span-2" placeholder="Company name" value={client.company} onChange={(e) => setClient({ ...client, company: e.target.value })} data-testid="client-company" />
            <input className="input-tech" placeholder="Contact person" value={client.name} onChange={(e) => setClient({ ...client, name: e.target.value })} data-testid="client-name" />
            <input className="input-tech" placeholder="Phone / email" value={client.contact} onChange={(e) => setClient({ ...client, contact: e.target.value })} data-testid="client-contact" />
            <input className="input-tech col-span-2" placeholder="Address" value={client.address} onChange={(e) => setClient({ ...client, address: e.target.value })} data-testid="client-address" />
            <input className="input-tech col-span-2" placeholder="GSTIN" value={client.gstin} onChange={(e) => setClient({ ...client, gstin: e.target.value })} data-testid="client-gstin" />
            <textarea className="input-tech col-span-2 h-24 py-2" placeholder="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="quote-notes" />
          </div>
        </div>
        <div className="bg-white p-6 flex flex-col justify-between">
          <div>
            <div className="label-tech mb-2">Summary</div>
            <div className="text-sm text-ink-muted mb-4">
              {items.length} line item{items.length !== 1 ? "s" : ""} · {cartTotals.count} rack{cartTotals.count !== 1 ? "s" : ""}
            </div>
            <div className="border-t border-line pt-3 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="label-tech">Weight</span>
                <span className="mono font-bold">{fmtNum(cartTotals.weight, 2)} kg</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="label-tech">Subtotal</span>
                <span className="mono font-bold">{fmtINR(cartTotals.price)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="label-tech">GST @ {fmtNum(gstPercent, 0)}%</span>
                <span className="mono font-bold">{fmtINR(gstAmount)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-line">
                <span className="label-tech">Grand total</span>
                <span className="display font-black text-2xl text-brand">{fmtINR(grandTotal)}</span>
              </div>
            </div>
          </div>
          <div className="mt-6 flex flex-col sm:flex-row gap-2">
            {canUseBom && (
              <button
                onClick={openBom}
                disabled={bomBusy || (items.length === 0 && !result)}
                className="btn-secondary w-full sm:w-auto sm:flex-1"
                data-testid="pr-bom-btn"
              >
                <FileText size={16} /> {bomBusy ? "Building…" : "BOM"}
              </button>
            )}
            <button
              onClick={saveQuote}
              disabled={saving || items.length === 0}
              className="btn-primary w-full sm:flex-1"
              data-testid="save-quote-btn"
            >
              <Save size={16} /> {saving ? "Saving…" : "Save quotation"}
            </button>
          </div>
        </div>
      </div>

      {bomOpen && (
        <PalletBomModal
          rows={bomRows}
          busy={bomBusy}
          client={client}
          items={items.length ? items : (result ? [{ config: cfg, quantity: Math.max(1, parseInt(quantity) || 1), label }] : [])}
          onClose={() => setBomOpen(false)}
        />
      )}
    </Layout>
  );
}

